import React, { useEffect, useState } from 'react';
import ErrorWrapper from '@/app/ErrorWrapper';
import Fullcalender from './FullCalendar';
import { useSelector, useDispatch } from 'react-redux';
import { studentsApi } from '@/api/students/studentsApi';
import ScheduledLessonModal from '../ScheduledLessonModal';
import { coursesApi, teachersApi } from '@/api';
import { getProfile } from '@/pages/Users/store/profileThunk';
import { useTranslation } from 'react-i18next';

const CalendarComponent = ({ ...props }) => {
    const { i18n, t } = useTranslation();
    const currentLanguageKey = i18n.language;
    const { user } = useSelector((state) => state.profile);
    const authData = useSelector((state) => state.auth);
    const dispatch = useDispatch();

    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const tokens = user?.tokens || [];

    const getEvents = async () => {
        if (authData?.roles[0].name == 'Admin') {
            const res = await coursesApi.getList({ page: 1, pageSize: 10 });
            const courses = res.data.data.rows;
            const events = courses
                .map((course) => {
                    const schedules = course.scheduleLessons;
                    return schedules?.map((schedule) => ({
                        id: schedule.id,
                        title: course.title,
                        start: schedule.startDate,
                        end: schedule.endDate,
                        className: 'bg-info',
                        extendedProps: {
                            id: schedule.id,
                            courseId: course.id,
                            courseName: course.title,
                            link: schedule.link,
                            description: schedule.description,
                            enrollment: course
                        }
                    }));
                })
                .flat();
            return events;
        } else if (authData?.roles[0].name == 'Student') {
            const res = await studentsApi.getDetail(user?.id);
            const enrollments = res.data.data.enrollments;

            const events = enrollments
                .map((enrollment) => {
                    const schedules = enrollment.course.scheduleLessons;
                    return schedules.map((schedule) => ({
                        id: schedule.id,
                        title: enrollment.course.title,
                        start: schedule.startDate,
                        end: schedule.endDate,
                        className: 'bg-info',
                        extendedProps: {
                            id: schedule.id,
                            courseId: enrollment.course.id,
                            courseName: enrollment.course.title,
                            link: schedule.link,
                            description: schedule.description,
                            enrollment: enrollment
                        }
                    }));
                })
                .flat();
            return events;
        } else if (authData?.roles[0].name == 'Teacher') {
            const res = await teachersApi.getDetail(user?.id);
            const enrollments = res.data.data.assignedCourses;

            const events = enrollments
                .map((enrollment) => {
                    const schedules = enrollment.scheduleLessons;
                    return schedules.map((schedule) => ({
                        id: schedule.id,
                        title: schedule.description,
                        start: schedule.startDate,
                        end: schedule.endDate,
                        className: 'bg-info',
                        extendedProps: {
                            id: schedule.id,
                            courseId: enrollment.id,
                            courseName: enrollment.title,
                            link: schedule.link,
                            description: schedule.description,
                            enrollment: enrollment
                        }
                    }));
                })
                .flat();
            return events;
        }
    };

    const handleEventClick = (info) => {
        setSelectedEvent(info.event);
        setIsModalVisible(true);
    };

    const handleModalClose = () => {
        setIsModalVisible(false);
        setSelectedEvent(null);
    };

    useEffect(() => {
        getEvents().then((events) => {
            setEvents(events);
        });
        if (authData?.roles[0].name == 'Student') {
            dispatch(getProfile({ ...user, roles: authData?.roles }));
        }
    }, []);

    useEffect(() => {
        if (user?.language) i18n.changeLanguage(user?.language);
    }, [user]);

    return (
        <>
            <div className="section-body">
                <div className="container-fluid">
                    <div className="d-flex justify-content-between align-items-center ">
                        <div className="header-action">
                            <h1 className="page-title">{t('Calendar_Title')}</h1>
                            <ol className="breadcrumb page-breadcrumb">
                                <li className="breadcrumb-item">
                                    <a href>{t('Calendar_BrandName')}</a>
                                </li>
                                <li
                                    className="breadcrumb-item active"
                                    aria-current="page">
                                    {t('Calendar_Title')}
                                </li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            <div className="section-body mt-4">
                {authData?.roles[0].name == 'Student' && (
                    <div className="flex">
                        {tokens?.map((item, idx) => (
                            <div
                                className="col-lg-3 col-md-6"
                                key={idx}>
                                <div className="card">
                                    <div className="card-body">
                                        <div className="widgets1">
                                            <div className="icon">
                                                <i className="icon-trophy text-success font-30"></i>
                                            </div>
                                            <div className="details">
                                                <h6 className="mb-0 font600">
                                                    {t(item?.tokenType?.name)}
                                                </h6>
                                                <span className="mb-0">
                                                    {t('Calendar_Credit')}: {item?.credit}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
                <div className="container-fluid">
                    <div className="row clearfix row-deck">
                        <div className="w-full">
                            <div className="card">
                                <div className="card-body">
                                    <Fullcalender
                                        events={events}
                                        onEventClick={handleEventClick}
                                        currentLocale={currentLanguageKey}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isModalVisible && (
                <ScheduledLessonModal
                    isVisible={isModalVisible}
                    onClose={handleModalClose}
                    selectedEvent={selectedEvent}
                />
            )}
        </>
    );
};

CalendarComponent.displayName = 'Calendar';

const Calendar = (props) => {
    return (
        <ErrorWrapper>
            <CalendarComponent
                data-id="Calendar-cpn"
                {...props}
            />
        </ErrorWrapper>
    );
};

export default Calendar;
